import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { Sentry } from '@org/utils-client';
import { cn } from '../../../../utils';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { Mic, Phone, PhoneOff, Loader2, MessageCircle, History, ChevronDown, ChevronUp } from 'lucide-react';
import { AudioSettingsPopover, useAudioSettings } from '../../../custom/audio-settings';
import { Button } from '../../../core/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../../core/tooltip';
import { ContentDescription } from '../shared/content-description';
import { ExerciseFeedbackList } from '../shared/exercise-feedback-list';
import { ElevenLabsConversationUI } from './elevenlabs-conversation-ui';
import { SessionResult } from './session-result';
import { SessionHistoryList } from './session-history-list';
import { useOpenAIRealtime } from './use-openai-realtime';
import { formatTime } from './conversational-exercise-types';
import { SpeakPanel, ZineButton } from '../../zine';
import { zineColors } from '../../zine/zine-theme';
import type {
  ConversationalExerciseProps,
  ConversationProvider,
  ConversationState,
  ConversationTurn,
  CompletionResult,
  SessionSummary,
  SessionWithTurns,
} from './conversational-exercise-types';

export type { ConversationalExerciseProps };

export function ConversationalExercise({
  content,
  feedback,
  variant = 'card',
  isReadOnly = false,
  viewerStudentId,
}: ConversationalExerciseProps) {
  const { t } = useTranslation('p2e');
  const [conversationState, setConversationState] = useState<ConversationState>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [completionResult, setCompletionResult] = useState<CompletionResult | null>(null);
  const [selectedHistorySessionId, setSelectedHistorySessionId] = useState<string | null>(null);
  const [showHistoryList, setShowHistoryList] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Provider-specific state
  const [provider, setProvider] = useState<ConversationProvider>('openai');
  const [ephemeralKey, setEphemeralKey] = useState<string | null>(null);
  const [elevenlabsContext, setElevenlabsContext] = useState<string | undefined>(undefined);

  // Audio device selection
  const { selectedMicId, setSelectedMicId, selectedSpeakerId, setSelectedSpeakerId, getMicConstraints, setSpeakerOnElement } = useAudioSettings();

  const turnsContainerRef = useRef<HTMLDivElement>(null);

  // OpenAI Realtime hook (WebRTC, push-to-talk, timer, idle detection).
  // Every way a call can end (End button, timer, inactivity, the AI, lost
  // connection, leaving the page) reports back through onEnding/onEnded, so the
  // screen always moves to the result instead of freezing.
  const openai = useOpenAIRealtime({
    maxDurationSec: content.maxDurationSec ?? 300,
    getMicConstraints,
    setSpeakerOnElement,
    onEnding: () => setConversationState('ending'),
    onEnded: ({ result, error: endError }) => {
      if (result) {
        setCompletionResult(result);
      } else if (endError) {
        setError(t('zine.speak.endedNoResult'));
      }
      setConversationState('completed');
    },
  });

  // tRPC queries for past sessions
  const { data: sessionsData, isLoading: isLoadingSessions } = trpc.conversation.getSessions.useQuery(
    {
      exerciseId: content.exerciseId,
      limit: 10,
      ...(viewerStudentId ? { studentId: viewerStudentId } : {}),
    },
    { staleTime: 1000 * 60 * 5 }
  );

  const { data: selectedSessionData, isLoading: isLoadingSelectedSession } = trpc.conversation.getSession.useQuery(
    { sessionId: selectedHistorySessionId! },
    { enabled: !!selectedHistorySessionId, staleTime: 1000 * 60 * 5 }
  );

  const startSessionMutation = trpc.conversation.startSession.useMutation();

  const previousSessions = useMemo<SessionSummary[]>(() => {
    if (!sessionsData) return [];
    return (sessionsData as SessionSummary[])
      .filter((s) => s.status === 'completed')
      .sort((a, b) => new Date(b.startedAt ?? 0).getTime() - new Date(a.startedAt ?? 0).getTime());
  }, [sessionsData]);

  // Auto-scroll to bottom when new turns are added
  useEffect(() => {
    if (turnsContainerRef.current) {
      turnsContainerRef.current.scrollTop = turnsContainerRef.current.scrollHeight;
    }
  }, [openai.turns]);

  // Leaving the page mid-call: finish the session on the server (evaluated from
  // what was saved) instead of leaving it hanging forever.
  useEffect(() => {
    return () => {
      if (openai.isActive()) {
        void openai.endConversation('unmount').catch(() => undefined);
      } else {
        openai.cleanup();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEndConversation = useCallback(() => {
    // State transitions happen in onEnding/onEnded (shared with every other end path).
    void openai.endConversation('user').catch(() => undefined);
  }, [openai]);

  const handleStartConversation = async () => {
    try {
      setConversationState('connecting');
      setError(null);
      openai.clearError();

      const session = await startSessionMutation.mutateAsync({
        exerciseId: content.exerciseId,
      });

      if (!session.sessionId || !session.ephemeralKey) {
        throw new Error('Invalid session response from server');
      }

      setSessionId(session.sessionId);
      setProvider((session.provider as ConversationProvider) || 'openai');
      setEphemeralKey(session.ephemeralKey);

      // For ElevenLabs, store the context and let ElevenLabsConversationUI handle connection
      if (session.provider === 'elevenlabs') {
        setElevenlabsContext(session.elevenlabsContext);
        setConversationState('active');
        return;
      }

      // For OpenAI: connect via WebRTC
      await openai.connect({
        sessionId: session.sessionId,
        ephemeralKey: session.ephemeralKey,
      });

      setConversationState('active');
    } catch (err) {
      console.error('Failed to start conversation:', err);

      // Mic-permission / device errors are user-fixable — report as warnings, not alerts
      const isMicError =
        err instanceof DOMException &&
        ['NotAllowedError', 'NotFoundError', 'NotReadableError'].includes(err.name);

      Sentry.captureException(err, {
        level: isMicError ? 'warning' : 'error',
        tags: { feature: 'conversational-exercise', stage: 'start' },
        extra: { exerciseId: content.exerciseId },
      });

      setError(
        isMicError
          ? 'We could not access your microphone. Please allow microphone access in your browser and try again.'
          : err instanceof Error
            ? err.message
            : 'Failed to start conversation'
      );
      setConversationState('idle');
      openai.cleanup();
    }
  };

  const handleStartNewConversation = () => {
    setConversationState('idle');
    setSessionId(null);
    setCompletionResult(null);
    setSelectedHistorySessionId(null);
    setError(null);
    openai.clearError();
    setProvider('openai');
    setEphemeralKey(null);
    setElevenlabsContext(undefined);
  };

  const handleViewHistorySession = (session: SessionSummary) => {
    if (session.sessionId) {
      setSelectedHistorySessionId(session.sessionId);
      setConversationState('viewing_history');
      setShowHistoryList(false);
    }
  };

  const handleBackFromHistory = () => {
    setSelectedHistorySessionId(null);
    setConversationState('idle');
  };

  const displayError = error || openai.error;
  const isStartPending = startSessionMutation.isPending;

  // ── Speak-panel presentation ──────────────────────────────────────────────

  const avatarInitial =
    (content.theme ?? content.title ?? '').trim().charAt(0).toUpperCase() || 'A';

  const panelTitle = content.title
    ? renderInlineMarkdown(content.title, 'conversational-exercise-title')
    : t('zine.speak.title');

  const descriptionLine = [
    content.theme ?? null,
    t('zine.speak.desc'),
    t('zine.speak.maxDuration', { time: formatTime(content.maxDurationSec ?? 300) }),
  ]
    .filter(Boolean)
    .join(' · ');

  const showIdleHistory =
    conversationState === 'idle' && !isReadOnly && previousSessions.length > 0;
  const showReadOnlyIdle =
    isReadOnly && conversationState === 'idle' && !selectedHistorySessionId;
  const showFeedbackList =
    Boolean(feedback && feedback.length > 0) &&
    conversationState !== 'completed' &&
    conversationState !== 'viewing_history';

  const hasExtras =
    Boolean(displayError) ||
    conversationState !== 'idle' ||
    (showIdleHistory && showHistoryList) ||
    showReadOnlyIdle ||
    showFeedbackList ||
    showIdleHistory;

  /** White paper card so the existing light session/history UIs sit well on ink. */
  const paperCard = (children: React.ReactNode) => (
    <div
      className="rounded-[6px] p-4"
      style={{ background: zineColors.paper, color: zineColors.ink }}
    >
      {children}
    </div>
  );

  return (
    <div className={cn(variant === 'inline' && 'my-2')}>
      <SpeakPanel
        initial={avatarInitial}
        title={panelTitle}
        description={descriptionLine}
        intro={
          content.description?.trim() ? (
            <ContentDescription text={content.description} markdown />
          ) : undefined
        }
        action={
          !isReadOnly ? (
            <div className="flex items-center gap-2">
              <AudioSettingsPopover
                selectedMicId={selectedMicId}
                onMicSelect={setSelectedMicId}
                selectedSpeakerId={selectedSpeakerId}
                onSpeakerSelect={setSelectedSpeakerId}
                showSpeakerSelection={true}
                disabled={conversationState === 'active' || conversationState === 'ending'}
              />
              {conversationState === 'idle' && (
                <ZineButton
                  onClick={handleStartConversation}
                  disabled={isStartPending}
                  className="inline-flex items-center gap-2"
                >
                  {isStartPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Phone className="h-4 w-4" />
                  )}
                  {isStartPending ? t('zine.speak.connecting') : t('zine.speak.start')}
                </ZineButton>
              )}
            </div>
          ) : undefined
        }
      >
        {hasExtras ? (
          <div className="space-y-4 pt-2">
            {displayError && (
              <div
                className="rounded-[4px] border p-3 text-sm font-medium"
                style={{
                  borderColor: zineColors.sizzle,
                  background: 'rgba(232,75,90,.12)',
                  color: zineColors.baby,
                }}
                role="alert"
              >
                {displayError}
              </div>
            )}

            {/* Idle state — previous attempts */}
            {showIdleHistory && (
              <div className="w-full">
                <button
                  type="button"
                  onClick={() => setShowHistoryList(!showHistoryList)}
                  className="mx-auto flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition hover:opacity-80"
                  style={{ color: zineColors.charm }}
                >
                  <History className="h-4 w-4" />
                  <span>{t('zine.speak.historyCount', { count: previousSessions.length })}</span>
                  {showHistoryList ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {showHistoryList && (
                  <div className="mx-auto mt-2 max-w-md">
                    {paperCard(
                      <SessionHistoryList
                        sessions={previousSessions}
                        onSelect={handleViewHistorySession}
                      />,
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Connecting state (OpenAI only) */}
            {conversationState === 'connecting' && provider !== 'elevenlabs' && (
              <div className="flex flex-col items-center gap-3 py-8">
                <Loader2 className="h-8 w-8 animate-spin" style={{ color: zineColors.charm }} />
                <p className="m-0 text-sm" style={{ color: '#D9C7CC' }}>
                  {t('zine.speak.connecting')}
                </p>
              </div>
            )}

            {/* ElevenLabs Conversation UI */}
            {(conversationState === 'active' || conversationState === 'connecting') &&
              provider === 'elevenlabs' &&
              sessionId &&
              ephemeralKey &&
              paperCard(
                <ElevenLabsConversationUI
                  content={content}
                  session={{ sessionId, ephemeralKey, elevenlabsContext }}
                  feedback={feedback}
                  variant={variant}
                  onEnd={handleEndConversation}
                  onTurnSaved={(role, turnContent) => {
                    // ElevenLabs manages its own turns; we don't need openai.turns here
                    void role;
                    void turnContent;
                  }}
                />,
              )}

            {/* OpenAI Conversation UI — Push-to-talk */}
            {(conversationState === 'active' || conversationState === 'ending') &&
              provider !== 'elevenlabs' && (
                <OpenAIActiveView
                  turns={openai.turns}
                  isRecording={openai.isRecording}
                  currentTranscript={openai.currentTranscript}
                  timeRemaining={openai.timeRemaining}
                  idleSecondsLeft={openai.idleSecondsLeft}
                  quietSeconds={openai.quietSeconds}
                  isWrappingUp={openai.isWrappingUp}
                  conversationState={conversationState}
                  turnsContainerRef={turnsContainerRef}
                  onEndConversation={handleEndConversation}
                  onToggleRecording={openai.handleToggleRecording}
                  onPointerDown={openai.handlePointerDown}
                  onPointerUp={openai.handlePointerUp}
                />
              )}

            {/* Completed state */}
            {conversationState === 'completed' &&
              completionResult &&
              paperCard(
                <SessionResult
                  session={null}
                  result={completionResult}
                  sessionTurns={openai.turns}
                  feedback={feedback}
                  onStartNew={handleStartNewConversation}
                  isReadOnly={isReadOnly}
                />,
              )}

            {/* Ended without a result (evaluation unavailable) — never a dead end */}
            {conversationState === 'completed' && !completionResult && !isReadOnly && (
              <div className="flex justify-center">
                <ZineButton onClick={handleStartNewConversation}>{t('zine.speak.startNew')}</ZineButton>
              </div>
            )}

            {/* Viewing history state */}
            {conversationState === 'viewing_history' &&
              selectedHistorySessionId &&
              (isLoadingSelectedSession ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 className="h-8 w-8 animate-spin" style={{ color: zineColors.charm }} />
                  <p className="m-0 text-sm" style={{ color: '#D9C7CC' }}>
                    {t('zine.speak.loadingConversation')}
                  </p>
                </div>
              ) : selectedSessionData ? (
                paperCard(
                  <SessionResult
                    session={selectedSessionData as SessionWithTurns}
                    result={null}
                    sessionTurns={[]}
                    feedback={feedback}
                    onBackFromHistory={handleBackFromHistory}
                    onStartNew={handleStartNewConversation}
                    isReadOnly={isReadOnly}
                  />,
                )
              ) : (
                <div className="py-6 text-center" style={{ color: '#D9C7CC' }}>
                  <p className="m-0">{t('zine.speak.sessionNotFound')}</p>
                  <Button
                    onClick={handleBackFromHistory}
                    variant="link"
                    className="mt-2"
                    style={{ color: zineColors.charm }}
                  >
                    {t('zine.speak.goBack')}
                  </Button>
                </div>
              ))}

            {/* Read-only mode with no active conversation */}
            {showReadOnlyIdle && (
              <ReadOnlyView
                isLoading={isLoadingSessions}
                previousSessions={previousSessions}
                onViewSession={handleViewHistorySession}
                paperCard={paperCard}
              />
            )}

            {/* Show feedback for non-completed states */}
            {showFeedbackList && feedback && <ExerciseFeedbackList feedback={feedback} />}
          </div>
        ) : null}
      </SpeakPanel>
    </div>
  );
}

// --- Sub-components (colocated, not exported) ---

export interface OpenAIActiveViewProps {
  turns: ConversationTurn[];
  isRecording: boolean;
  currentTranscript: string;
  timeRemaining: number;
  idleSecondsLeft: number | null;
  quietSeconds: number;
  isWrappingUp: boolean;
  conversationState: ConversationState;
  turnsContainerRef: React.RefObject<HTMLDivElement | null>;
  onEndConversation: () => void;
  onToggleRecording: () => void;
  onPointerDown: () => void;
  onPointerUp: () => void;
}

/** After this many quiet seconds (nobody talking, AI idle) the mic label nudges the student. */
const QUIET_HINT_SEC = 10;

export function OpenAIActiveView({
  turns,
  isRecording,
  currentTranscript,
  timeRemaining,
  idleSecondsLeft,
  quietSeconds,
  isWrappingUp,
  conversationState,
  turnsContainerRef,
  onEndConversation,
  onToggleRecording,
  onPointerDown,
  onPointerUp,
}: OpenAIActiveViewProps) {
  const { t } = useTranslation('p2e');
  // Nudge only when the line is truly quiet (off-mic: nobody talking; on-mic: no voice
  // reaching the open mic) and no stronger notice (countdown / wrap-up) is on screen.
  const showQuietHint = quietSeconds >= QUIET_HINT_SEC && idleSecondsLeft === null && !isWrappingUp;
  // Keep the pointer on the button while holding, so a finger drifting off the
  // edge does not cut the recording in half.
  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // ignore: capture is best-effort
    }
    onPointerDown();
  };
  return (
    <div className="flex min-h-[400px] flex-col">
      {/* Conversation transcript */}
      <div
        ref={turnsContainerRef}
        className={cn(
          'flex-1 space-y-3 overflow-y-auto rounded-t-[6px] p-4 transition-colors duration-300 sm:p-5',
          isRecording
            ? 'bg-gradient-to-b from-rose-950/5 to-rose-900/10'
            : 'bg-gradient-to-b from-slate-50 to-white'
        )}
        style={{ maxHeight: '280px', minHeight: '120px', background: isRecording ? undefined : '#FFF8F5' }}
      >
        {turns.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center py-6 text-center">
            <p className="text-sm text-slate-400">
              {isRecording ? 'Listening...' : 'Tap the mic button below to speak'}
            </p>
          </div>
        ) : (
          turns.map((turn, index) => (
            <div
              key={index}
              className={cn(
                'flex animate-in fade-in slide-in-from-bottom-2 duration-300',
                turn.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              <div
                className={cn(
                  'max-w-[85%] rounded-2xl px-4 py-2.5',
                  turn.role === 'user'
                    ? 'text-white'
                    : 'border bg-white shadow-sm'
                )}
                style={
                  turn.role === 'user'
                    ? { background: zineColors.ink }
                    : { borderColor: zineColors.line, color: zineColors.ink }
                }
              >
                {turn.pending ? (
                  <p className="m-0 animate-pulse text-sm leading-relaxed opacity-70">…</p>
                ) : turn.failed ? (
                  <p className="m-0 text-xs italic leading-relaxed opacity-70">{t('zine.speak.transcriptFailed')}</p>
                ) : (
                  <p className="m-0 text-sm leading-relaxed">{turn.content}</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Inactivity countdown (last minute only) / wrap-up notice */}
      {idleSecondsLeft !== null && !isRecording && (
        <div
          className="px-4 py-2 text-center text-xs font-semibold"
          style={{ background: 'rgba(232,75,90,.12)', color: zineColors.sizzle }}
          role="status"
        >
          {t('zine.speak.idleCountdown', { seconds: idleSecondsLeft })}
        </div>
      )}
      {idleSecondsLeft === null && isWrappingUp && (
        <div
          className="px-4 py-2 text-center text-xs font-semibold"
          style={{ background: '#FEF3EF', color: zineColors.warmGray }}
          role="status"
        >
          {t('zine.speak.wrappingUp')}
        </div>
      )}

      {/* Push-to-Talk Control Zone */}
      <div className={cn(
        'rounded-b-[6px] px-4 py-4 transition-all duration-300 sm:py-6',
        isRecording
          ? 'bg-gradient-to-t from-rose-600 via-rose-500 to-rose-400'
          : 'border-t'
      )}
        style={!isRecording ? { background: '#FEF3EF', borderColor: zineColors.line } : undefined}
      >
        {/* Timer and End button */}
        <div className="mb-4 flex items-center justify-between">
          <div className={cn(
            'flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
            isRecording ? 'bg-white/20 text-white' : ''
          )}
            style={!isRecording ? { background: '#fff', border: `1px solid ${zineColors.line}`, color: zineColors.warmGray } : undefined}
          >
            <div className={cn(
              'h-1.5 w-1.5 shrink-0 rounded-full',
              isRecording ? 'animate-pulse bg-white' : ''
            )}
              style={!isRecording ? { background: zineColors.good } : undefined}
            />
            <span className="font-mono tracking-wide">{formatTime(timeRemaining)}</span>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={onEndConversation}
                disabled={conversationState === 'ending' || isRecording}
                variant="ghost"
                size="sm"
                className={cn(
                  'h-auto shrink-0 gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium',
                  isRecording && 'bg-white/20 text-white/80 hover:bg-white/30'
                )}
                style={!isRecording ? { background: '#fff', border: `1px solid ${zineColors.line}`, color: zineColors.warmGray } : undefined}
              >
                <PhoneOff className="h-3.5 w-3.5" />
                <span>End</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {conversationState === 'ending' ? 'Ending...' : 'End conversation'}
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Large central push-to-talk button */}
        <div className="mb-3 flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={onToggleRecording}
                onPointerDown={handlePointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                disabled={conversationState === 'ending'}
                size="xl"
                className={cn(
                  'relative touch-none select-none rounded-full transition-all duration-200 active:scale-95',
                  isRecording
                    ? 'bg-white text-rose-600 shadow-2xl shadow-rose-900/30 hover:bg-white'
                    : 'text-white shadow-xl'
                )}
                style={!isRecording ? { background: zineColors.ink } : undefined}
              >
                {isRecording && (
                  <>
                    <span className="absolute inset-0 animate-ping rounded-full bg-white/40" />
                    <span className="absolute -inset-3 animate-pulse rounded-full border-2 border-white/30" />
                    <span className="absolute -inset-6 animate-pulse rounded-full border border-white/20" style={{ animationDelay: '150ms' }} />
                  </>
                )}
                <Mic className={cn(
                  'relative z-10 transition-transform',
                  isRecording ? 'h-9 w-9 sm:h-10 sm:w-10' : 'h-8 w-8 sm:h-9 sm:w-9'
                )} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isRecording ? 'Release or tap to send' : 'Tap or hold to speak'}
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Status text */}
        <div className={cn(
          'h-5 text-center transition-colors duration-200',
          isRecording ? 'text-white/90' : ''
        )}
          style={!isRecording ? { color: zineColors.warmGray } : undefined}
        >
          {isRecording ? (
            <span className="animate-pulse text-sm font-medium" role={showQuietHint ? 'status' : undefined}>
              {showQuietHint ? t('zine.speak.goAhead') : 'Recording... release or tap to send'}
            </span>
          ) : showQuietHint ? (
            <span className="animate-pulse text-sm font-semibold" style={{ color: zineColors.ink }} role="status">
              {t('zine.speak.goAhead')}
            </span>
          ) : currentTranscript ? (
            <span className="flex items-center justify-center gap-2 text-sm">
              <span className="flex gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-rose-400" style={{ animationDelay: '0ms' }} />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-rose-400" style={{ animationDelay: '100ms' }} />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-rose-400" style={{ animationDelay: '200ms' }} />
              </span>
              {currentTranscript}
            </span>
          ) : (
            <span className="text-sm">Tap or hold to speak</span>
          )}
        </div>
      </div>
    </div>
  );
}

interface ReadOnlyViewProps {
  isLoading: boolean;
  previousSessions: SessionSummary[];
  onViewSession: (session: SessionSummary) => void;
  paperCard: (children: React.ReactNode) => React.ReactNode;
}

function ReadOnlyView({ isLoading, previousSessions, onViewSession, paperCard }: ReadOnlyViewProps) {
  const { t } = useTranslation('p2e');

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: zineColors.charm }} />
        <p className="m-0 text-sm" style={{ color: '#D9C7CC' }}>
          {t('zine.speak.loadingSessions')}
        </p>
      </div>
    );
  }

  if (previousSessions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full"
          style={{ background: 'rgba(242,160,196,.2)' }}
        >
          <MessageCircle className="h-7 w-7" style={{ color: zineColors.charm }} />
        </div>
        <p className="m-0 text-sm" style={{ color: '#D9C7CC' }}>
          {t('zine.speak.noSessions')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <div className="w-full max-w-md space-y-3">
        <div
          className="flex items-center justify-center gap-2 text-sm"
          style={{ color: '#D9C7CC' }}
        >
          <History className="h-4 w-4" />
          <span>{t('zine.speak.attempts', { count: previousSessions.length })}</span>
        </div>
        {paperCard(
          <SessionHistoryList sessions={previousSessions} onSelect={onViewSession} />,
        )}
      </div>
    </div>
  );
}
